import type {
  DashboardAlert,
  DashboardAlertType,
  DashboardMetrics,
} from "@fiscalo/shared-types";
import { parseLocalDateOnly } from "@/lib/dateFormat";
import type { FiscalAgendaObligation } from "@/services/fiscalService";

export const DASHBOARD_MAX_UNIFIED_ALERTS = 10;

export const TAX_I18N: Record<string, string> = {
  IVA: "fiscalAgenda.tax.iva",
  IRAE: "fiscalAgenda.tax.irae",
  BPS: "fiscalAgenda.tax.bps",
  IRPF: "fiscalAgenda.tax.irpf",
  IRNR: "fiscalAgenda.tax.irnr",
  MONOTRIBUTO: "fiscalAgenda.tax.monotributo",
};

const SEVERITY_ORDER: Record<string, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

const API_ALERT_MESSAGE_KEYS: Partial<Record<DashboardAlertType, string>> = {
  repeated_rejections: "dashboard.alertsBar.rejectedByDgi",
  pending_retries: "dashboard.alertsBar.pendingRetry",
  cae_expiring: "dashboard.alertsBar.caeExpiring",
  certificate_expiring: "dashboard.alertsBar.certificateExpiring",
  daily_report_failed: "dashboard.alertsBar.dailyReportFailed",
  subscription_past_due: "dashboard.alertsBar.subscriptionLapsed",
};

const API_ALERT_MESSAGE_KEYS_WARNING: Partial<Record<DashboardAlertType, string>> = {
  daily_report_failed: "dashboard.alertsBar.dailyReportRetrying",
  subscription_past_due: "dashboard.alertsBar.subscriptionPastDue",
};

export type FiscalAlertContext = {
  overdueCount: number;
  dueTodayCount: number;
  dueIn1Count: number;
  dueIn3Count: number;
  dueIn7Count: number;
  nextObligation: FiscalAgendaObligation | null;
  singleOverdue?: FiscalAgendaObligation;
  singleDueToday?: FiscalAgendaObligation;
  singleDueTomorrow?: FiscalAgendaObligation;
  singleDueIn3?: FiscalAgendaObligation;
  singleDueIn7?: FiscalAgendaObligation;
};

export type UnifiedAlertItem = {
  id: string;
  messageKey?: string;
  messageOptions?: Record<string, number | string>;
  message?: string;
  detailMessageKey?: string;
  obligationForDetail?: FiscalAgendaObligation;
  actionUrl: string;
  variant: "error" | "warning" | "info";
  dismissible: boolean;
  ctaKey: string;
};

/**
 * Flatten the month-keyed agenda into one list sorted by due date.
 *
 * Accepts undefined so callers can hand it a React Query result directly —
 * three of the four copies this replaced had to guard at the call site, and
 * the fourth (DashboardFiscalHealthScore) diverged instead: it skipped the
 * sort entirely and returned obligations in object-key order. That was
 * harmless there only because every consumer filters rather than indexes.
 */
export function flattenAgenda(
  grouped: Record<string, FiscalAgendaObligation[]> | undefined
): FiscalAgendaObligation[] {
  if (!grouped) return [];
  const list: FiscalAgendaObligation[] = [];
  for (const key of Object.keys(grouped).sort()) {
    for (const o of grouped[key] ?? []) {
      list.push(o);
    }
  }
  return list.sort(
    (a, b) =>
      parseLocalDateOnly(a.dueDate).getTime() -
      parseLocalDateOnly(b.dueDate).getTime()
  );
}

/** When authority is set, only obligations for that authority (e.g. DGI) are counted. */
export function computeFiscalAlertContext(
  grouped: Record<string, FiscalAgendaObligation[]> | undefined,
  authority?: "DGI" | "BPS"
): FiscalAlertContext | undefined {
  if (!grouped) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const in3 = new Date(today);
  in3.setDate(in3.getDate() + 3);
  const in7 = new Date(today);
  in7.setDate(in7.getDate() + 7);
  let list = flattenAgenda(grouped).filter((o) => o.status !== "completed");
  if (authority) {
    list = list.filter((o) => o.authority === authority);
  }
  let overdueCount = 0;
  let dueTodayCount = 0;
  let dueIn1Count = 0;
  let dueIn3Count = 0;
  let dueIn7Count = 0;
  let firstOverdue: FiscalAgendaObligation | undefined;
  let firstDueToday: FiscalAgendaObligation | undefined;
  let firstDueTomorrow: FiscalAgendaObligation | undefined;
  let firstDueIn3: FiscalAgendaObligation | undefined;
  let firstDueIn7: FiscalAgendaObligation | undefined;
  for (const o of list) {
    const due = parseLocalDateOnly(o.dueDate);
    if (due < today) {
      overdueCount += 1;
      if (overdueCount === 1) firstOverdue = o;
    } else if (due.getTime() === today.getTime()) {
      dueTodayCount += 1;
      if (dueTodayCount === 1) firstDueToday = o;
    } else if (due.getTime() === tomorrow.getTime()) {
      dueIn1Count += 1;
      if (dueIn1Count === 1) firstDueTomorrow = o;
    } else if (due <= in3) {
      dueIn3Count += 1;
      if (dueIn3Count === 1) firstDueIn3 = o;
    } else if (due <= in7) {
      dueIn7Count += 1;
      if (dueIn7Count === 1) firstDueIn7 = o;
    }
  }
  return {
    overdueCount,
    dueTodayCount,
    dueIn1Count,
    dueIn3Count,
    dueIn7Count,
    nextObligation: list[0] ?? null,
    singleOverdue: overdueCount === 1 ? firstOverdue : undefined,
    singleDueToday: dueTodayCount === 1 ? firstDueToday : undefined,
    singleDueTomorrow: dueIn1Count === 1 ? firstDueTomorrow : undefined,
    singleDueIn3: dueIn3Count === 1 ? firstDueIn3 : undefined,
    singleDueIn7: dueIn7Count === 1 ? firstDueIn7 : undefined,
  };
}

function alertCtaKey(type: DashboardAlertType): string {
  const keyMap: Record<DashboardAlertType, string> = {
    repeated_rejections: "dashboard.alerts.cta.reviewRejected",
    pending_retries: "dashboard.alerts.cta.viewInvoices",
    cae_expiring: "dashboard.alerts.cta.reviewCae",
    certificate_expiring: "dashboard.alerts.cta.uploadCertificate",
    daily_report_failed: "dashboard.alerts.cta.reviewDailyReports",
    subscription_past_due: "dashboard.alerts.cta.updatePayment",
  };
  return keyMap[type] ?? "dashboard.alerts.cta.viewInvoices";
}

export function buildUnifiedAlerts(
  metrics: DashboardMetrics,
  apiAlerts: DashboardAlert[],
  fiscalContext?: FiscalAlertContext
): UnifiedAlertItem[] {
  const items: UnifiedAlertItem[] = [];
  if (fiscalContext) {
    if (fiscalContext.overdueCount > 0) {
      const one =
        fiscalContext.overdueCount === 1 ? fiscalContext.singleOverdue : undefined;
      items.push({
        id: "obligation-overdue",
        ...(one
          ? {
              detailMessageKey: "dashboard.alertsBar.overdueOneDetail",
              obligationForDetail: one,
            }
          : {
              messageKey:
                fiscalContext.overdueCount === 1
                  ? "dashboard.alertsBar.overdueOne"
                  : "dashboard.alertsBar.overdueMany",
              messageOptions:
                fiscalContext.overdueCount === 1
                  ? undefined
                  : { count: fiscalContext.overdueCount },
            }),
        actionUrl: "/agenda",
        variant: "error",
        dismissible: false,
        ctaKey: "dashboard.quickActions.openFiscalAgenda",
      });
    }
    if (fiscalContext.dueTodayCount > 0) {
      const one =
        fiscalContext.dueTodayCount === 1
          ? fiscalContext.singleDueToday
          : undefined;
      items.push({
        id: "obligation-due-today",
        ...(one
          ? {
              detailMessageKey: "dashboard.alertsBar.dueTodayOneDetail",
              obligationForDetail: one,
            }
          : {
              messageKey:
                fiscalContext.dueTodayCount === 1
                  ? "dashboard.alertsBar.dueTodayOne"
                  : "dashboard.alertsBar.dueTodayMany",
              messageOptions:
                fiscalContext.dueTodayCount === 1
                  ? undefined
                  : { count: fiscalContext.dueTodayCount },
            }),
        actionUrl: "/agenda",
        variant: "warning",
        dismissible: false,
        ctaKey: "dashboard.quickActions.openFiscalAgenda",
      });
    }
    if (fiscalContext.dueIn1Count > 0) {
      const one =
        fiscalContext.dueIn1Count === 1
          ? fiscalContext.singleDueTomorrow
          : undefined;
      items.push({
        id: "obligation-due-in-1",
        ...(one
          ? {
              detailMessageKey: "dashboard.alertsBar.dueTomorrowOneDetail",
              obligationForDetail: one,
            }
          : {
              messageKey:
                fiscalContext.dueIn1Count === 1
                  ? "dashboard.alertsBar.dueTomorrowOne"
                  : "dashboard.alertsBar.dueTomorrowMany",
              messageOptions:
                fiscalContext.dueIn1Count === 1
                  ? undefined
                  : { count: fiscalContext.dueIn1Count },
            }),
        actionUrl: "/fiscal-status",
        variant: "warning",
        dismissible: false,
        ctaKey: "dashboard.quickActions.openFiscalStatus",
      });
    }
    if (fiscalContext.dueIn3Count > 0) {
      const one =
        fiscalContext.dueIn3Count === 1 ? fiscalContext.singleDueIn3 : undefined;
      items.push({
        id: "obligation-due-in-3",
        ...(one
          ? {
              detailMessageKey: "dashboard.alertsBar.dueIn3OneDetail",
              obligationForDetail: one,
            }
          : {
              messageKey:
                fiscalContext.dueIn3Count === 1
                  ? "dashboard.alertsBar.dueIn3One"
                  : "dashboard.alertsBar.dueIn3Many",
              messageOptions:
                fiscalContext.dueIn3Count === 1
                  ? undefined
                  : { count: fiscalContext.dueIn3Count },
            }),
        actionUrl: "/agenda",
        variant: "warning",
        dismissible: false,
        ctaKey: "dashboard.quickActions.openFiscalAgenda",
      });
    }
    if (fiscalContext.dueIn7Count > 0) {
      const one =
        fiscalContext.dueIn7Count === 1 ? fiscalContext.singleDueIn7 : undefined;
      items.push({
        id: "obligation-due-in-7",
        ...(one
          ? {
              detailMessageKey: "dashboard.alertsBar.dueIn7OneDetail",
              obligationForDetail: one,
            }
          : {
              messageKey:
                fiscalContext.dueIn7Count === 1
                  ? "dashboard.alertsBar.dueIn7One"
                  : "dashboard.alertsBar.dueIn7Many",
              messageOptions:
                fiscalContext.dueIn7Count === 1
                  ? undefined
                  : { count: fiscalContext.dueIn7Count },
            }),
        actionUrl: "/agenda",
        variant: "warning",
        dismissible: false,
        ctaKey: "dashboard.quickActions.openFiscalAgenda",
      });
    }
  }
  const dismissibleApiTypes: DashboardAlertType[] = [
    "repeated_rejections",
    "pending_retries",
  ];
  for (const a of apiAlerts) {
    if (items.some((i) => i.id === a.id)) continue;
    const variant =
      a.severity === "error"
        ? "error" as const
        : a.severity === "warning"
          ? "warning" as const
          : "info" as const;
    const messageKey =
      (variant === "warning" && API_ALERT_MESSAGE_KEYS_WARNING[a.type]) ||
      API_ALERT_MESSAGE_KEYS[a.type];
    items.push({
      id: a.id,
      messageKey: messageKey ?? undefined,
      message: messageKey ? undefined : a.message,
      actionUrl: a.actionUrl ?? "/dashboard",
      variant,
      dismissible: dismissibleApiTypes.includes(a.type),
      ctaKey: alertCtaKey(a.type),
    });
  }
  const sorted = [...items].sort(
    (a, b) =>
      (SEVERITY_ORDER[a.variant] ?? 3) - (SEVERITY_ORDER[b.variant] ?? 3)
  );
  return sorted.slice(0, DASHBOARD_MAX_UNIFIED_ALERTS);
}
